import {
	type ColumnDef,
	DataTable,
	FilterBuilder,
	type FilterField,
	SearchInput,
	useFilterBuilder,
	useUrlPagination,
} from "@monbits/ui";
import { Coins, Network } from "lucide-react";
import * as React from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import CopyButton from "src/components/copy-button/copy-button";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import TimeLabel from "src/components/time-label/time-label";
import type { OrphanTransaction } from "src/providers/orphan-transaction-provider";
import { useOrphanTransactions } from "src/queries/orphan-transaction-queries";
import { cryptoTickerOptions } from "src/utils/options";

const NETWORK_OPTIONS = [
	{ value: "BTC", label: "Bitcoin" },
	{ value: "ETH", label: "Ethereum" },
	{ value: "MATIC", label: "Polygon" },
	{ value: "BNB", label: "BNB Chain" },
	{ value: "TRON", label: "TRON" },
];

const FILTER_FIELDS: FilterField[] = [
	{
		id: "blockchain",
		label: "Network",
		type: "select",
		icon: <Network className="h-4 w-4" />,
		options: NETWORK_OPTIONS,
	},
	{
		id: "currency",
		label: "Currency",
		type: "select",
		icon: <Coins className="h-4 w-4" />,
		options: cryptoTickerOptions,
	},
];

const truncate = (s: string, head = 8, tail = 6) =>
	s.length <= head + tail ? s : `${s.slice(0, head)}...${s.slice(-tail)}`;

const columns: ColumnDef<OrphanTransaction>[] = [
	{
		id: "id",
		header: "ID",
		cell: (row) => <span className="font-medium tabular-nums">#{row.id}</span>,
	},
	{
		id: "network",
		header: "Network",
		cell: (row) => (
			<div className="flex items-center gap-2">
				<CryptoIcon ticker={row.blockchain} className="h-5 w-5" />
				<span className="text-sm">{row.blockchain}</span>
			</div>
		),
	},
	{
		id: "hash",
		header: "Hash",
		cell: (row) =>
			row.transactionHash ? (
				<div className="flex items-center gap-1">
					<span className="font-mono text-xs">
						{truncate(row.transactionHash, 10, 8)}
					</span>
					<CopyButton value={row.transactionHash} label="Hash" />
				</div>
			) : (
				<span className="text-xs text-muted-foreground">—</span>
			),
	},
	{
		id: "amount",
		header: "Amount",
		cell: (row) => (
			<div className="flex items-center gap-2">
				<CryptoIcon ticker={row.currency} className="h-4 w-4" />
				<span className="font-mono text-sm tabular-nums">
					{row.factAmount ?? row.amount}
				</span>
			</div>
		),
	},
	{
		id: "recipient",
		header: "Recipient",
		cell: (row) => (
			<span className="font-mono text-xs">
				{truncate(row.recipientAddress)}
			</span>
		),
	},
	{
		id: "received",
		header: "Received",
		cell: (row) => <TimeLabel time={row.createdAt} />,
	},
];

const UnattributedDepositsPage: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const { filters, setFilters } = useFilterBuilder({
		fields: FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const search = searchParams.get("search") || "";

	const apiFilters = React.useMemo(() => {
		const result: { blockchain?: string; currency?: string } = {};
		for (const f of filters) {
			if (f.operator === "is") {
				if (f.field === "blockchain") result.blockchain = f.value;
				else if (f.field === "currency") result.currency = f.value;
			}
		}
		return result;
	}, [filters]);

	const [totalCount, setTotalCount] = React.useState(0);
	const { page, limit, offset, setPage, setLimit } = useUrlPagination({
		totalCount,
	});

	const listQuery = useOrphanTransactions({
		search: search || undefined,
		blockchain: apiFilters.blockchain,
		currency: apiFilters.currency,
		limit,
		offset,
	});

	const items = listQuery.data?.items ?? [];

	React.useEffect(() => {
		if (listQuery.data) setTotalCount(listQuery.data.totalCount);
	}, [listQuery.data]);

	const handleRowClick = React.useCallback(
		(row: OrphanTransaction) => {
			const qs = searchParams.toString();
			navigate(`/unattributed-deposits/${row.id}${qs ? `?${qs}` : ""}`);
		},
		[navigate, searchParams],
	);

	return (
		<div className="p-4 sm:p-6 space-y-6">
			<div>
				<h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
					Unattributed Deposits
				</h1>
				<p className="text-sm text-muted-foreground mt-1 max-w-2xl">
					Incoming on-chain transactions that arrived after order expiry or to
					stale addresses. Open one to credit it to the right merchant.
				</p>
			</div>

			<div className="flex gap-4 flex-wrap items-center">
				<SearchInput
					placeholder="Search by hash, address, or sender..."
					paramName="search"
					className="w-full sm:w-[350px]"
				/>
				<FilterBuilder
					fields={FILTER_FIELDS}
					filters={filters}
					onFiltersChange={setFilters}
				/>
			</div>

			<DataTable
				columns={columns}
				data={items}
				isLoading={listQuery.isLoading}
				onRowClick={handleRowClick}
				getRowId={(row) => String(row.id)}
				emptyMessage={
					search || filters.length > 0
						? "No deposits match your search or filters."
						: "No unattributed deposits. Incoming transactions that aren't matched to an order or payment will appear here."
				}
				pagination={{
					page,
					pageSize: limit,
					totalCount,
					onPageChange: setPage,
					onPageSizeChange: setLimit,
				}}
			/>

			<Outlet />
		</div>
	);
};

export default UnattributedDepositsPage;
